import { Button, Dialog, DialogActions, DialogContent, DialogTitle, InputLabel } from '@material-ui/core';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import _ from 'lodash';
import { formatDate } from 'helpers/DateFormatter';
import AlertDialog from 'components/DialogComponent/AlertDialog';
import { MESSAGE_TYPE } from 'journeys/common/common.constants';
import TextInput from '../../elements/TextInput';
import { useStyles } from '../AdminForm.styled';
import { trackErrors } from '../helper';
import { AdminConstants } from 'journeys/portal/Admin/AdminScreen.constants';
import { DatePickerForForm } from 'elements/Inputs/DatePicker/DatepickerForForm';
import DropdownInput from '../../elements/DropdownInput';
import { APIEndpoints } from 'helpers/APILists';

const AnnouncementsForm = ({ isOpen, title, onClose, rowData, onRecordSubmit }) => {
    const classes = useStyles();
    const [createRowData, setCreateRowData] = useState(rowData);
    const [cancelAlert, setCancelAlert] = useState(false);
    const { instance } = useMsal();
    const userName = instance.getActiveAccount();

    const FooterButtonVisible = !_.isEqual(rowData, createRowData) && createRowData.FeatureName
        && createRowData.Announcement && createRowData.NewsDate && createRowData.Status;

    const handleInputChange = (e) => {
        setCreateRowData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleDateChange = (name, value) => {
        setCreateRowData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitButton = () => {
        const payload = {
            ...createRowData,
            LastModifiedBy: userName?.name,
            LastModifiedDate: formatDate(new Date()),
            NewsDate: formatDate(new Date(createRowData.NewsDate)),
            Status: createRowData.Status
        };
        if (createRowData.RowId) {
            const { id, ...existingRecordPayload } = payload;
            updateRecord(existingRecordPayload);
        } else {
            const { RowId, ...newRecordPayload } = payload;
            newRecordPayload.CreatedBy = userName?.name;
            newRecordPayload.CreatedDate = formatDate(new Date());
            submitRecord(newRecordPayload);
        }
    };

    const resetButtonHandler = () => {
        setCreateRowData(rowData);
    };

    const cancelButtonHandler = () => {
        if (_.isEqual(rowData, createRowData)) {
            onClose();
        } else {
            setCancelAlert(true);
        }
    };

    const handleUnsaveChanges = () => {
        setCancelAlert(false);
    };

    const fetchAndHandleActiveAnnouncements = (res) => {
        axios.get(APIEndpoints.ADMIN_GetAnnouncements)
            .then((response) => {
                const activeAnnouncements = response.data
                    .filter(a => a.Status === 'Active')
                    .sort((a, b) => {
                        const dateComparison = new Date(b.NewsDate) - new Date(a.NewsDate);
                        return dateComparison !== 0 ? dateComparison : new Date(b.CreatedDate) - new Date(a.CreatedDate);
                    });
                const recentlyUpdatedRowId = res.data.RowId;
                const announcementsToConsider = activeAnnouncements.filter(a => a.RowId !== recentlyUpdatedRowId);
                if (announcementsToConsider.length >= 10) {
                    const announcementToDeactivate = announcementsToConsider[announcementsToConsider.length - 1];
                    deactivateOldestAnnouncement(announcementToDeactivate, res);
                } else {
                    onClose();
                    onRecordSubmit(res.data.RowId);
                }
            })
            .catch((err) => handleError(err));
    };

    const deactivateOldestAnnouncement = (announcement, res) => {
        axios.put(APIEndpoints.ADMIN_UpdateAnnouncements, {
            ...announcement,
            Status: 'Inactive',
            LastModifiedBy: userName?.name,
            LastModifiedDate: formatDate(new Date())
        })
            .then(() => {
                onClose();
                onRecordSubmit(res.data.RowId);
            })
            .catch((err) => handleError(err));
    };

    const handleError = (err) => {
        trackErrors({
            messageType: MESSAGE_TYPE.ERROR,
            message: err.message,
            exception: err
        });
    };

    const submitRecord = (dataRow) => {
        axios.post(APIEndpoints.ADMIN_AddAnnouncements, dataRow)
            .then((res) => fetchAndHandleActiveAnnouncements(res))
            .catch((err) => handleError(err));
    };

    const updateRecord = (dataRow) => {
        axios.put(APIEndpoints.ADMIN_UpdateAnnouncements, dataRow)
            .then((res) => fetchAndHandleActiveAnnouncements(res))
            .catch((err) => handleError(err));
    };

    return (
        <Dialog open={isOpen} scroll="paper" maxWidth="lg">
            <DialogTitle id="scroll-dialog-title" classes={{ root: classes.headerRoot }} className={classes.header}>
                {title}
            </DialogTitle>
            {cancelAlert && (
                <AlertDialog
                    dialogMessage={AdminConstants.DIALOG_MESSAGE}
                    dialogOpenState={cancelAlert}
                    handleClose={() => handleUnsaveChanges()}
                    handleAlertFunction={onClose}
                />
            )}

            <DialogContent>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <InputLabel style={{ fontSize: '12px', color: 'black', paddingLeft: '3px', paddingBottom: '0', fontWeight: '600' }}>
                            News Date
                        </InputLabel>
                        <DatePickerForForm
                            name="NewsDate"
                            title="News Date"
                            value={createRowData.NewsDate || null}
                            isRequired
                            onChange={(date) => handleDateChange('NewsDate', date)}
                            textAlign="center"
                            width="95%"
                            fontSize="11px"
                            height="27px"
                            borderStyle="none"
                            borderRadius="3px"
                            marginTop="0px"
                        />
                    </div>
                    <TextInput
                        name="FeatureName"
                        title="Feature Name"
                        value={createRowData.FeatureName}
                        required
                        handleInputChange={handleInputChange}
                        type="string"
                        maxLength="30"
                        isMultiline
                    />
                    <DropdownInput
                        name="Status"
                        title="Status"
                        value={createRowData.Status}
                        options={['Active', 'Inactive']}
                        handleInputChange={handleInputChange}
                        required
                    />
                </div>
                <div style={{ display: 'grid', marginTop: '1px', marginBottom: '10px' }}>
                    <TextInput
                        name="Announcement"
                        title="Announcement"
                        value={createRowData.Announcement}
                        required
                        handleInputChange={handleInputChange}
                        isMultiline
                        width="1080px"
                        type="string"
                        maxLength="1500"
                    />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 10px' }}>
                    <TextInput
                        name="CreatedBy"
                        title="Created By"
                        value={createRowData.CreatedBy || userName?.name}
                        disabled
                    />
                    <TextInput
                        name="CreatedDate"
                        title="Created Date"
                        value={createRowData.CreatedDate || formatDate(new Date())}
                        disabled
                    />
                    <TextInput
                        name="LastModifiedBy"
                        title="Last Modified By"
                        value={createRowData.LastModifiedBy || userName?.name}
                        disabled
                    />
                    <TextInput
                        name="LastModifiedDate"
                        title="Last Modified Date"
                        value={createRowData.LastModifiedDate || formatDate(new Date())}
                        disabled
                    />
                </div>
                <div style={{ display: 'grid', marginTop: '10px' }}>
                    <TextInput
                        name="Comment"
                        title="Comment"
                        value={createRowData.Comment}
                        handleInputChange={handleInputChange}
                        isMultiline
                        width="1080px"
                    />
                </div>
            </DialogContent>
            <DialogActions style={{ marginRight: '15px', paddingBottom: '18px', paddingTop: '15px' }}>
                <div style={{ display: 'flex', gap: '0 8px' }}>
                    {FooterButtonVisible && (
                        <Button classes={{ root: classes.button }} onClick={handleSubmitButton} variant="outlined">
                            Submit
                        </Button>
                    )}
                    <Button classes={{ root: classes.button }} onClick={resetButtonHandler} variant="outlined">
                        Reset
                    </Button>
                    <Button classes={{ root: classes.button }} onClick={cancelButtonHandler} variant="outlined">
                        Close
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default AnnouncementsForm;

AnnouncementsForm.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onRecordSubmit: PropTypes.func,
    rowData: PropTypes.object,
    title: PropTypes.string
};
